import Action from "../base/action";
import LoadTimeline from "../base/load-timeline";
import Prune from "../base/prune";
import { ResourceTypeEnum } from "../base/training-literals";

class ObjectProximityTrigger extends Action {
    until;
    timelineMap;
    prune;
    object1;
    object2;
    distanceThreshold;
    meshType;

    constructor(props) {
        super(props);
        this.until = props.until;
        this.distanceThreshold = props.distanceThreshold;
        this.object1 = props.object1;
        this.object2 = props.object2;
        this.objects = [props.object1, props.object2];
        this.prune = new Prune(props.prune || {});
        this.timelineMap = props.timelines && new LoadTimeline(props);
        this.meshType = {
            VOLUME: "Volume",
            TREE: "Branch",
            GROUP: "Group",
            MODEL: "Model"
        };
    }

    destroy() {
        this?.objects?.forEach((unused, index) => this.removeHandlersByType(index));
    }

    findPartialMatch(array, partialStr) {
        return array.find(item => item.includes(partialStr));
    }

    addHandlerOperationAgainstVolume(index) {
        const object = this.objects[index];
        const uuid = generateUUID();
        const modelId = MeshHierarchyViewFeatureData.getModelIdFromBranchID(object.id);
        const modelObject = getModelObject(modelId);
        object.addHandlers.push(`${modelId}move_model_from_volume${uuid}`);

        addHandlerForOperation(
            modelId,
            null,
            this.handleThisObjectProximityTrigger.bind(this),
            `${modelId}move_model_from_volume${uuid}`,
            HANDLER_TYPE_ENUM.MODEL_MOVE,
            HANDLER_TYPE_ENUM.MODEL_MOVE
        );

        if (modelObject?.isPointCloudModel) {
            return;
        }

        object.addHandlers.push(`${object.id}move_volume${uuid}`);
        addHandlerForOperation(
            object.id,
            null,
            this.handleThisObjectProximityTrigger.bind(this),
            `${object.id}move_volume${uuid}`,
            HANDLER_TYPE_ENUM.VOLUME_MOVE,
            HANDLER_TYPE_ENUM.VOLUME_MOVE
        );
    }

    removeHandlerOperationAgainstVolume(index) {
        const object = this.objects[index];
        const modelId = MeshHierarchyViewFeatureData.getModelIdFromBranchID(object.id);
        const modelObject = getModelObject(modelId);
        removeHandlerForOperation(modelId, this.findPartialMatch(object.addHandlers, `${modelId}move_model_from_volume`), HANDLER_TYPE_ENUM.MODEL_MOVE);

        if (modelObject?.isPointCloudModel) {
            return;
        }

        object.addHandlers.forEach(handlerName => {
            removeHandlerForOperation(object.id, handlerName, HANDLER_TYPE_ENUM.VOLUME_MOVE);
        });
    }

    setVolumeBoundingBox(index) {
        const object = this.objects[index];
        const modelId = MeshHierarchyViewFeatureData.getModelIdFromBranchID(object.id);
        const modelObject = getModelObject(modelId);

        if (modelObject?.isPointCloudModel) {
            object.boundingBox = BoundingBoxFeatureManager.getModelBoundingBox([modelId]).box;
            return;
        }

        let branchBoundingBox = new THREE.Box3();
        let volumeBoundingBox = new THREE.Box3();

        branchBoundingBox.makeEmpty();

        wv.rootObject.updateMatrixWorld(true);
        volumeBoundingBox.copy(getCurrentBoundingBoxOfVolumesInWorld(object.id));
        volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());

        branchBoundingBox.expandByPoint(volumeBoundingBox.min);
        branchBoundingBox.expandByPoint(volumeBoundingBox.max);

        this.objects[index].boundingBox = branchBoundingBox;
    }

    getResourceId(index) {
        const object = this.objects[index];
        const trainingModelId = object.id;
        const resources = this.eventLoop.training.resources;
        const resource = resources ? resources[trainingModelId] : null;
        return resource?.resourceId || (resource?.type === ResourceTypeEnum.HELPER_OBJECT && resource?.id);
    }


    addHandlerOperationAgainstModel(index) {
        const object = this.objects[index];
        const resourceId = this.getResourceId(index);
        const uuid = generateUUID();
        const modelObject = getModelObject(resourceId);
        object.addHandlers.push(`${object.id}move_model${uuid}`);

        addHandlerForOperation(
            resourceId,
            null,
            this.handleThisObjectProximityTrigger.bind(this),
            `${object.id}move_model${uuid}`,
            HANDLER_TYPE_ENUM.MODEL_MOVE,
            HANDLER_TYPE_ENUM.MODEL_MOVE
        );

        if (modelObject?.isPointCloudModel) {
            return;
        }

        modelObject?.volumes.forEach(volumeData => {
            object.addHandlers.push(`${volumeData.branchID}move_model${uuid}`);
            addHandlerForOperation(
                volumeData.branchID,
                null,
                this.handleThisObjectProximityTrigger.bind(this),
                `${volumeData.branchID}move_model${uuid}`,
                HANDLER_TYPE_ENUM.VOLUME_MOVE,
                HANDLER_TYPE_ENUM.VOLUME_MOVE
            )
        });
    }

    removeHandlerOperationAgainstModel(index) {
        const object = this.objects[index];
        const resourceId = this.getResourceId(index);
        const modelObject = getModelObject(resourceId);

        object.addHandlers.forEach(handlerName => {
            removeHandlerForOperation(resourceId, handlerName, HANDLER_TYPE_ENUM.MODEL_MOVE);
        });

        if (modelObject?.isPointCloudModel) {
            return;
        }

        object.addHandlers.forEach(handlerName => {
            const branchId = handlerName.split('move_model')[0];
            removeHandlerForOperation(branchId, handlerName, HANDLER_TYPE_ENUM.VOLUME_MOVE);
        });
    }

    setModelBoundingBox(index) {
        const resourceId = this.getResourceId(index);
        let modelBoundingBox = new THREE.Box3();
        let volumeBoundingBox = new THREE.Box3();
        let modelObject = getModelObject(resourceId);

        modelBoundingBox.makeEmpty();
        wv.rootObject.updateMatrixWorld(true);
        modelObject.updateMatrixWorld(true);

        if (modelObject.meshType === MeshTypeEnum.POINT_CLOUD) {
            modelObject.traverse(function (child) {
                if (child instanceof Potree.PointCloudOctree && child.boundingBox) {
                    child.updateMatrixWorld(true);
                    volumeBoundingBox.copy(child.boundingBox);
                    volumeBoundingBox.applyMatrix4(child.matrixWorld);
                    volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());
                    modelBoundingBox.expandByPoint(volumeBoundingBox.min);
                    modelBoundingBox.expandByPoint(volumeBoundingBox.max);
                }
            });
        } else {
            const volumes = modelManager.getVolumesByModelId(resourceId);
            for (const volume of volumes) {
                if (volume.meshType === MeshTypeEnum.POINT_CLOUD) {
                    continue;
                }

                volumeBoundingBox.copy(getCurrentBoundingBoxOfVolumesInWorld (volume.branchID));
                volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());
                modelBoundingBox.expandByPoint(volumeBoundingBox.min);
                modelBoundingBox.expandByPoint(volumeBoundingBox.max);
            }
        }

        this.objects[index].boundingBox = modelBoundingBox;
    }

    addHandlerOperationAgainstGroup(index) {
        const object = this.objects[index];
        const group = GroupData.getGroupById(object.id);
        const uuid = generateUUID();

        group?.volumes?.map(eachVol => eachVol.branchId).forEach(branch => {
            const node = MeshHierarchyViewFeatureData.getDataFromBranchID(branch);
            const leaves = node?.getLeaves();
            leaves.forEach(leaf => {
                object.addHandlers.push(`${leaf}move_volume${uuid}`);
                addHandlerForOperation(
                    leaf,
                    null,
                    this.handleThisObjectProximityTrigger.bind(this),
                    `${leaf}move_volume${uuid}`,
                    HANDLER_TYPE_ENUM.VOLUME_MOVE,
                    HANDLER_TYPE_ENUM.VOLUME_MOVE
                );
            });
        });

        object.addHandlers.push(`${group.modelData.id}move_model_from_group${uuid}`);
        addHandlerForOperation(
            group.modelData.id,
            null,
            this.handleThisObjectProximityTrigger.bind(this),
            `${group.modelData.id}move_model_from_group${uuid}`,
            HANDLER_TYPE_ENUM.MODEL_MOVE,
            HANDLER_TYPE_ENUM.MODEL_MOVE
        );
    }

    setGroupBoundingBox(index) {
        const object = this.objects[index];
        const group = GroupData.getGroupById(object.id);
        const volumes = group.volumes;
        const modelID = group.modelData.id;
        let groupBoundingBox = new THREE.Box3();
        let volumeBoundingBox = new THREE.Box3();
        let modelObject = getModelObject(modelID);

        groupBoundingBox.makeEmpty();
        wv.rootObject.updateMatrixWorld(true);

        if (modelObject.meshType === MeshTypeEnum.POINT_CLOUD) {
            let modelBoundingBox = new THREE.Box3();
            modelObject.traverse(function (child) {
                if (child instanceof Potree.PointCloudOctree && child.boundingBox) {
                    child.updateMatrixWorld(true);
                    volumeBoundingBox.copy(child.boundingBox);
                    volumeBoundingBox.applyMatrix4(child.matrixWorld);
                    volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());
                    modelBoundingBox.expandByPoint(volumeBoundingBox.min);
                    modelBoundingBox.expandByPoint(volumeBoundingBox.max);
                }
            });
        } else {
            for(const volume of volumes) {
                volumeBoundingBox.copy(getCurrentBoundingBoxOfVolumesInWorld(volume.branchId));
                volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());
                groupBoundingBox.expandByPoint(volumeBoundingBox.min);
                groupBoundingBox.expandByPoint(volumeBoundingBox.max);
            }
        }

        this.objects[index].boundingBox = groupBoundingBox;
    }

    addHandlerOperationAgainstBranch(index) {
        const object = this.objects[index];
        const node = MeshHierarchyViewFeatureData.getDataFromBranchID(object.id);
        const leaves = node?.getLeaves();
        const uuid = generateUUID();
        const modelObject = getModelObject(node.modelId);

        object.addHandlers.push(`${node.modelId}move_model_from_branch${uuid}`);
        addHandlerForOperation(
            node.modelId,
            null,
            this.handleThisObjectProximityTrigger.bind(this),
            `${node.modelId}move_model_from_branch${uuid}`,
            HANDLER_TYPE_ENUM.MODEL_MOVE,
            HANDLER_TYPE_ENUM.MODEL_MOVE
        );

        if (modelObject?.isPointCloudModel) {
            return;
        }

        leaves.forEach(leaf => {
            object.addHandlers.push(`${leaf}move_volume${uuid}`);
            addHandlerForOperation(
                leaf,
                null,
                this.handleThisObjectProximityTrigger.bind(this),
                `${leaf}move_volume${uuid}`,
                HANDLER_TYPE_ENUM.VOLUME_MOVE,
                HANDLER_TYPE_ENUM.VOLUME_MOVE
            );
        });
    }

    removeHandlerOperationAgainstGroupAndBranch(index, type) {
        const object = this.objects[index];
        const modelId = type === this.meshType.GROUP ? GroupData.getGroupById(object.id).modelData.id : MeshHierarchyViewFeatureData.getDataFromBranchID(object.id).modelId;
        const modelObject = getModelObject(modelId);

        removeHandlerForOperation(modelId, this.findPartialMatch(object.addHandlers, `${modelId}move_model_from_${type.toLowerCase()}`), HANDLER_TYPE_ENUM.MODEL_MOVE);

        if (modelObject?.isPointCloudModel) {
            return;
        }

        object.addHandlers.forEach(handlerName => {
            const leaf = handlerName.split('move_volume')[0];
            removeHandlerForOperation(leaf, handlerName, HANDLER_TYPE_ENUM.VOLUME_MOVE);
        });
    }

    setBranchBoundingBox(index) {
        const object = this.objects[index];
        const node = MeshHierarchyViewFeatureData.getDataFromBranchID(object.id);
        const modelId = MeshHierarchyViewFeatureData.getModelIdFromBranchID(node.branchID);
        const modelObject = getModelObject(modelId);

        if (modelObject?.isPointCloudModel) {
            object.boundingBox = BoundingBoxFeatureManager.getModelBoundingBox([modelId]).box;
            return;
        }

        const leaves = node?.getLeaves();
        let branchBoundingBox = new THREE.Box3();
        let volumeBoundingBox = new THREE.Box3();

        branchBoundingBox.makeEmpty();
        wv.rootObject.updateMatrixWorld(true);
        modelObject.updateMatrixWorld(true);

        if (modelObject.meshType === MeshTypeEnum.POINT_CLOUD) {

        } else {
            volumeBoundingBox.copy(getCurrentBoundingBoxOfVolumesInWorld(leaves));
            volumeBoundingBox.applyMatrix4(wv.rootObject.matrixWorld.clone().invert());
            branchBoundingBox.expandByPoint(volumeBoundingBox.min);
            branchBoundingBox.expandByPoint(volumeBoundingBox.max);
        }

        this.objects[index].boundingBox = branchBoundingBox;
    }

    removeHandlersByType(index) {
        const object = this.objects[index];
        switch (object.type) {
            case this.meshType.VOLUME:
                this.removeHandlerOperationAgainstVolume(index);
                break;
            case this.meshType.MODEL:
                this.removeHandlerOperationAgainstModel(index);
                break;
            case this.meshType.GROUP:
                this.removeHandlerOperationAgainstGroupAndBranch(index, this.meshType.GROUP);
                break;
            case this.meshType.TREE:
                this.removeHandlerOperationAgainstGroupAndBranch(index, this.meshType.TREE);
                break;
            default:
                console.warn("Unknown object type:", object.type);
        }

    }


    processFirstTime(index) {
        const object = this.objects[index];
        switch (object.type) {
            case this.meshType.VOLUME:
                this.addHandlerOperationAgainstVolume(index);
                this.setVolumeBoundingBox(index);
                break;
            case this.meshType.MODEL:
                this.addHandlerOperationAgainstModel(index);
                this.setModelBoundingBox(index);
                break;
            case this.meshType.GROUP:
                this.addHandlerOperationAgainstGroup(index);
                this.setGroupBoundingBox(index);
                break;
            case this.meshType.TREE:
                this.addHandlerOperationAgainstBranch(index);
                this.setBranchBoundingBox(index);
                break;
            default:
                console.warn("Unknown object type:", object.type);
        }
    }

    processObjectByType(index) {
        const object = this.objects[index];
        switch (object.type) {
            case this.meshType.VOLUME:
                this.setVolumeBoundingBox(index);
                break;
            case this.meshType.MODEL:
                this.setModelBoundingBox(index);
                break;
            case this.meshType.GROUP:
                this.setGroupBoundingBox(index);
                break;
            case this.meshType.TREE:
                this.setBranchBoundingBox(index);
                break;
            default:
                console.warn("Unknown object type:", object.type);
        }
    }

    getDistanceOfObjects() {
        if (this.object1.boundingBox.intersectsBox(this.object2.boundingBox)) {
            return 0;
        }

        const dx = Math.max(0, this.object1.boundingBox.min.x - this.object2.boundingBox.max.x, this.object2.boundingBox.min.x - this.object1.boundingBox.max.x);
        const dy = Math.max(0, this.object1.boundingBox.min.y - this.object2.boundingBox.max.y, this.object2.boundingBox.min.y - this.object1.boundingBox.max.y);
        const dz = Math.max(0, this.object1.boundingBox.min.z - this.object2.boundingBox.max.z, this.object2.boundingBox.min.z - this.object1.boundingBox.max.z);

        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }


    async getNextTimeline() {
        const nextAction = this.timelineMap;

        if (nextAction) {
            this.objects.forEach((unused, index) => this.removeHandlersByType(index));
            await nextAction.execute(this.eventLoop);
        }
    }

    async triggerNextTimeLine() {
        const distance = this.getDistanceOfObjects();

        if (distance <= this.distanceThreshold) {
            await this.getNextTimeline();
        }
    }

    async handleThisObjectProximityTrigger() {
        if (!this?.objects) {
            return;
        }

        this?.objects.forEach((unused, index) => this.processObjectByType(index));
        await this.triggerNextTimeLine();
    }

    async execute(eventLoop) {
        this.eventLoop = eventLoop;
        this.object1.boundingBox = new THREE.Box3();
        this.object2.boundingBox = new THREE.Box3();
        this.object1.addHandlers = [];
        this.object2.addHandlers = [];
        this.objects = [this.object1, this.object2];
        this.objects.forEach((unused, index) => this.processFirstTime(index));
        await this.triggerNextTimeLine();

        setTimeout(this.destroy, this.until - this.startTime);
    }

    async stop() {
        this.destroy();
    }

}


export default ObjectProximityTrigger;